import {
  ArrowUpRight,
  BookOpen,
  Box,
  Braces,
  Columns3,
  Database,
  FileCode2,
  GitMerge,
  Layers3,
  Play,
  Search,
  ServerCog,
  TableProperties
} from 'lucide-react';
import { useDeferredValue, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode, type UIEvent } from 'react';
import type { SessionStatus } from '../shared/portalTypes';
import catalogDocument from '../../catalog/mcp-tools.json';
import sourceCatalogDocument from '../../catalog/mcp-tool-sources.json';
import { McpMetadataWorkbench, WIDGET_TABLE_NAMES, type WidgetTableName } from './mcpMetadataWorkbench';
import { GOVERNED_TOOL_NAMES, McpGovernedWorkbench, type GovernedToolName } from './mcpGovernedWorkbench';
import { ANALYTICS_TOOL_NAMES, McpAnalyticsWorkbench, type AnalyticsToolName } from './mcpAnalyticsWorkbench';

interface McpToolOutputGroup {
  name: string;
  columns: string[];
}

interface McpToolCatalogEntry {
  name: string;
  title: string;
  domain: string;
  kind: string;
  service: string;
  summary: string;
  sourceObjects: string[];
  relationships: string[];
  freshness: string;
  coverage: string;
  outputGroups: McpToolOutputGroup[];
}

interface McpToolSource {
  label: string;
  repository: string;
  path: string;
  type: string;
}

interface McpToolSourceEntry {
  name: string;
  sources: McpToolSource[];
}

const TOOLS = catalogDocument.tools as McpToolCatalogEntry[];
const QUERY_SOURCES = new Map(
  (sourceCatalogDocument.tools as McpToolSourceEntry[]).map((tool) => [tool.name, tool.sources])
);
const ALL_FILTER = 'All';
const GITHUB_ROOT = 'https://github.com/iasset-com';
const METADATA_TOOLS = new Set(['describe_analytics_schema', 'list_widget_tables', 'describe_widget_table']);
const GOVERNED_TOOLS = new Set<string>(GOVERNED_TOOL_NAMES);
const ANALYTICS_TOOLS = new Set<string>(ANALYTICS_TOOL_NAMES);
const EXECUTABLE_TOOLS = new Set([...METADATA_TOOLS, ...GOVERNED_TOOLS, ...ANALYTICS_TOOLS]);
const METADATA_TOOL_NAMES = ['describe_analytics_schema', 'list_widget_tables', 'describe_widget_table'] as const;
const MCP_WORKSPACE_STORAGE_KEY = 'iasset.portal.mcp-tools.workspace.v1';
const MCP_INDEX_SCROLL_KEY = 'iasset.portal.mcp-tools.index-scroll.v1';
const normalizeSearchText = (value: string) => value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

interface McpWorkspaceState {
  query: string;
  domain: string;
  kind: string;
  selectedName: string;
  selectedWidgetTable: WidgetTableName;
  activeTab: 'overview' | 'run';
}

export function McpToolsView({ sessionStatus, onConnect }: {
  sessionStatus: SessionStatus;
  onConnect(): void;
}): ReactNode {
  const [workspace, setWorkspace] = useState<McpWorkspaceState>(() => readMcpWorkspaceState());
  const { query, domain, kind, selectedName, selectedWidgetTable, activeTab } = workspace;
  const indexRef = useRef<HTMLDivElement>(null);
  const detailRef = useRef<HTMLElement>(null);
  const detailScrollPositions = useRef(new Map<string, number>());
  const normalizedQuery = normalizeSearchText(query);
  const deferredQuery = useDeferredValue(normalizedQuery);
  const searchTransitionPending = normalizedQuery !== deferredQuery;

  const domains = useMemo(() => [ALL_FILTER, ...new Set(TOOLS.map((tool) => tool.domain))], []);
  const kinds = useMemo(() => [ALL_FILTER, ...new Set(TOOLS.map((tool) => tool.kind))], []);
  const filteredTools = useMemo(() => TOOLS.filter((tool) => {
    if (domain !== ALL_FILTER && tool.domain !== domain) return false;
    if (kind !== ALL_FILTER && tool.kind !== kind) return false;
    if (!deferredQuery) return true;
    const searchable = normalizeSearchText([
      tool.name,
      tool.title,
      tool.domain,
      tool.kind,
      tool.service,
      tool.summary,
      ...tool.sourceObjects,
      ...tool.relationships,
      ...tool.outputGroups.flatMap((group) => group.columns),
      ...(QUERY_SOURCES.get(tool.name) ?? []).flatMap((source) => [source.label, source.repository, source.path, source.type])
    ].join(' '));
    return searchable.includes(deferredQuery);
  }), [deferredQuery, domain, kind]);

  const selectedTool = TOOLS.find((tool) => tool.name === selectedName) ?? filteredTools[0] ?? TOOLS[0];
  const sourceCount = new Set(TOOLS.flatMap((tool) => tool.sourceObjects)).size;
  const contextualSchemaDrillDown = selectedName === 'describe_widget_table'
    && normalizeSearchText(query).includes('list widget tables');

  const updateWorkspace = (patch: Partial<McpWorkspaceState>, history: 'push' | 'replace' = 'replace') => {
    setWorkspace((current) => {
      const next = normalizeMcpWorkspaceState({ ...current, ...patch });
      writeMcpWorkspaceState(next, history);
      return next;
    });
  };

  useEffect(() => {
    const restoreRouteState = () => {
      if (window.location.hash.replace(/^#/, '').split('?', 1)[0] === 'mcp-tools') {
        const restored = readMcpWorkspaceState();
        setWorkspace(restored);
        writeMcpWorkspaceState(restored, 'replace');
      }
    };
    window.addEventListener('hashchange', restoreRouteState);
    window.addEventListener('popstate', restoreRouteState);
    return () => {
      window.removeEventListener('hashchange', restoreRouteState);
      window.removeEventListener('popstate', restoreRouteState);
    };
  }, []);

  useLayoutEffect(() => {
    const index = indexRef.current;
    if (index) {
      index.scrollTop = readStoredNumber(MCP_INDEX_SCROLL_KEY);
    }
  }, []);

  useLayoutEffect(() => {
    const detail = detailRef.current;
    if (!detail) return;
    detail.scrollTop = detailScrollPositions.current.get(selectedName) ?? 0;
  }, [selectedName]);

  const rememberIndexScroll = (event: UIEvent<HTMLDivElement>) => {
    window.sessionStorage.setItem(MCP_INDEX_SCROLL_KEY, String(event.currentTarget.scrollTop));
  };

  const rememberDetailScroll = (event: UIEvent<HTMLElement>) => {
    detailScrollPositions.current.set(selectedName, event.currentTarget.scrollTop);
  };

  useEffect(() => {
    writeMcpWorkspaceState(workspace, 'replace');
  }, []);

  useEffect(() => {
    if (
      !searchTransitionPending
      && filteredTools.length > 0
      && !filteredTools.some((tool) => tool.name === selectedName)
      && !contextualSchemaDrillDown
    ) {
      updateWorkspace({
        selectedName: filteredTools[0].name,
        activeTab: EXECUTABLE_TOOLS.has(filteredTools[0].name) ? 'run' : 'overview'
      });
    }
  }, [contextualSchemaDrillDown, filteredTools, searchTransitionPending, selectedName]);

  return (
    <section className="mcp-tools-view" aria-labelledby="mcp-tools-title">
      <header className="mcp-tools-header">
        <div className="mcp-tools-heading">
          <span className="mcp-tools-eyebrow"><Database aria-hidden="true" /> Data lineage catalog</span>
          <h2 id="mcp-tools-title">MCP tools, from source to surface</h2>
          <p>Understand what each tool returns, which service owns it, and how business objects relate, then open the exact query implementation in GitHub.</p>
        </div>
        <div className="mcp-tools-metrics" aria-label="MCP tool catalog summary">
          <span><strong>{TOOLS.length}</strong> tools</span>
          <span><strong>{domains.length - 1}</strong> domains</span>
          <span><strong>{sourceCount}</strong> source objects</span>
          <span><strong>{kinds.length - 1}</strong> tool types</span>
        </div>
      </header>

      <div className="mcp-tools-controls">
        <label className="mcp-tools-search">
          <Search aria-hidden="true" />
          <span className="sr-only">Search MCP tools, output columns, and query sources</span>
          <input
            value={query}
            onChange={(event) => updateWorkspace({ query: event.target.value })}
            placeholder="Search tool, source object, output column, or query file"
          />
          <kbd>{filteredTools.length}</kbd>
        </label>
        <label className="mcp-tools-select">
          <span>Domain</span>
          <select value={domain} onChange={(event) => updateWorkspace({ domain: event.target.value })}>
            {domains.map((value) => <option key={value} value={value}>{value}</option>)}
          </select>
        </label>
        <div className="mcp-kind-filter" aria-label="Tool type filter">
          {kinds.map((value) => (
            <button
              key={value}
              className={kind === value ? 'selected' : undefined}
              type="button"
              onClick={() => updateWorkspace({ kind: value })}
            >
              {value}
            </button>
          ))}
        </div>
      </div>

      {filteredTools.length === 0 ? (
        <div className="mcp-tools-empty">
          <Search aria-hidden="true" />
          <strong>No tools match this view</strong>
          <span>Try a source object such as ContractItem, a domain, or an output column.</span>
          <button type="button" onClick={() => updateWorkspace({ query: '', domain: ALL_FILTER, kind: ALL_FILTER })}>Clear filters</button>
        </div>
      ) : (
        <div className="mcp-tools-workbench">
          <aside className="mcp-tool-index" aria-label="MCP tool index">
            <div className="mcp-tool-index-header">
              <span>Tool index</span>
              <strong>{filteredTools.length}</strong>
            </div>
            <div ref={indexRef} className="mcp-tool-index-scroll" onScroll={rememberIndexScroll}>
              {filteredTools.map((tool) => (
                <button
                  key={tool.name}
                  className={tool.name === selectedTool?.name ? 'selected' : undefined}
                  type="button"
                  onClick={() => updateWorkspace({
                    selectedName: tool.name,
                    activeTab: EXECUTABLE_TOOLS.has(tool.name) ? 'run' : 'overview'
                  }, 'push')}
                  aria-current={tool.name === selectedTool?.name ? 'true' : undefined}
                >
                  <span className="mcp-tool-index-icon"><ToolKindIcon kind={tool.kind} /></span>
                  <span className="mcp-tool-index-copy">
                    <code>{tool.name}</code>
                    <small>{tool.domain} · {tool.kind}</small>
                  </span>
                  <span className="mcp-tool-column-count">{countOutputColumns(tool)}</span>
                </button>
              ))}
            </div>
          </aside>

          {selectedTool && (
            <McpToolDetail
              detailRef={detailRef}
              tool={selectedTool}
              sources={QUERY_SOURCES.get(selectedTool.name) ?? []}
              sessionStatus={sessionStatus}
              initialWidgetTable={selectedWidgetTable}
              activeTab={activeTab}
              onConnect={onConnect}
              onDetailScroll={rememberDetailScroll}
              onTabChange={(tab) => updateWorkspace({ activeTab: tab }, 'push')}
              onInspectWidgetTable={(tableName) => {
                updateWorkspace({
                  selectedWidgetTable: tableName,
                  selectedName: 'describe_widget_table',
                  activeTab: 'run'
                }, 'push');
              }}
            />
          )}
        </div>
      )}
    </section>
  );
}

function McpToolDetail({
  detailRef,
  tool,
  sources,
  sessionStatus,
  initialWidgetTable,
  activeTab,
  onConnect,
  onDetailScroll,
  onTabChange,
  onInspectWidgetTable
}: {
  detailRef: React.RefObject<HTMLElement | null>;
  tool: McpToolCatalogEntry;
  sources: McpToolSource[];
  sessionStatus: SessionStatus;
  initialWidgetTable: WidgetTableName;
  activeTab: 'overview' | 'run';
  onConnect(): void;
  onDetailScroll(event: UIEvent<HTMLElement>): void;
  onTabChange(tab: 'overview' | 'run'): void;
  onInspectWidgetTable(tableName: WidgetTableName): void;
}): ReactNode {
  const executable = EXECUTABLE_TOOLS.has(tool.name);

  return (
    <article ref={detailRef} className="mcp-tool-detail" aria-labelledby={`mcp-tool-${tool.name}`} onScroll={onDetailScroll}>
      <header className="mcp-tool-detail-header">
        <div>
          <div className="mcp-tool-badges">
            <span>{tool.domain}</span>
            <span>{tool.kind}</span>
          </div>
          <code id={`mcp-tool-${tool.name}`}>{tool.name}</code>
          <h3>{tool.title}</h3>
          <p>{tool.summary}</p>
        </div>
        <div className="mcp-tool-owner">
          <ServerCog aria-hidden="true" />
          <span>Owned by</span>
          <strong>{tool.service}</strong>
        </div>
      </header>

      <div className="mcp-tool-operations" aria-label="Data contract notes">
        <span><Layers3 aria-hidden="true" /><small>Freshness</small><strong>{tool.freshness}</strong></span>
        <span><Box aria-hidden="true" /><small>Coverage</small><strong>{tool.coverage}</strong></span>
        <span><Columns3 aria-hidden="true" /><small>Surface</small><strong>{countOutputColumns(tool)} output columns</strong></span>
      </div>

      {executable && (
        <div className="mcp-tool-tabs" role="tablist" aria-label="MCP tool workspace">
          <button type="button" role="tab" aria-selected={activeTab === 'overview'} className={activeTab === 'overview' ? 'selected' : undefined} onClick={() => onTabChange('overview')}>
            <BookOpen aria-hidden="true" /><span>Overview</span>
          </button>
          <button type="button" role="tab" aria-selected={activeTab === 'run'} className={activeTab === 'run' ? 'selected' : undefined} onClick={() => onTabChange('run')}>
            <Play aria-hidden="true" /><span>Run</span><em>Read only</em>
          </button>
        </div>
      )}

      {METADATA_TOOL_NAMES.map((toolName) => (
        <div
          key={toolName}
          hidden={!executable || activeTab !== 'run' || tool.name !== toolName}
          inert={!executable || activeTab !== 'run' || tool.name !== toolName ? true : undefined}
        >
          <McpMetadataWorkbench
            toolName={toolName}
            sessionStatus={sessionStatus}
            initialWidgetTable={initialWidgetTable}
            onConnect={onConnect}
            onInspectWidgetTable={onInspectWidgetTable}
          />
        </div>
      ))}
      {GOVERNED_TOOL_NAMES.map((toolName) => (
        <div
          key={toolName}
          hidden={!executable || activeTab !== 'run' || tool.name !== toolName}
          inert={!executable || activeTab !== 'run' || tool.name !== toolName ? true : undefined}
        >
          <McpGovernedWorkbench
            toolName={toolName as GovernedToolName}
            sessionStatus={sessionStatus}
            onConnect={onConnect}
          />
        </div>
      ))}
      {ANALYTICS_TOOL_NAMES.map((toolName) => (
        <div
          key={toolName}
          hidden={!executable || activeTab !== 'run' || tool.name !== toolName}
          inert={!executable || activeTab !== 'run' || tool.name !== toolName ? true : undefined}
        >
          <McpAnalyticsWorkbench
            toolName={toolName as AnalyticsToolName}
            sessionStatus={sessionStatus}
            onConnect={onConnect}
          />
        </div>
      ))}
      {(!executable || activeTab === 'overview') && (
        <McpToolOverview tool={tool} sources={sources} />
      )}
    </article>
  );
}

function McpToolOverview({ tool, sources }: { tool: McpToolCatalogEntry; sources: McpToolSource[] }): ReactNode {
  return <>

      <section className="mcp-query-source-section">
        <div className="mcp-query-source-heading">
          <div><FileCode2 aria-hidden="true" /><span><h4>Query sources</h4><p>Exact SQL, query builders, procedures, or schema definitions on the current develop branch.</p></span></div>
          <span className="mcp-query-verified">GitHub verified</span>
        </div>
        <div className="mcp-query-source-list">
          {sources.map((source) => (
            <a
              key={`${source.repository}/${source.path}`}
              href={`${GITHUB_ROOT}/${source.repository}/blob/develop/${source.path}`}
              target="_blank"
              rel="noreferrer"
            >
              <span className="mcp-query-source-icon"><Braces aria-hidden="true" /></span>
              <span className="mcp-query-source-copy">
                <span><strong>{source.label}</strong><em>{source.type}</em></span>
                <small>{source.repository}</small>
                <code>{source.path}</code>
              </span>
              <ArrowUpRight aria-hidden="true" />
            </a>
          ))}
        </div>
      </section>

      <section className="mcp-lineage-section">
        <div className="mcp-section-heading">
          <TableProperties aria-hidden="true" />
          <div><h4>Source objects</h4><p>Tables, snapshots, views, or composed tools read by this result.</p></div>
        </div>
        <div className="mcp-source-object-list">
          {tool.sourceObjects.map((source) => <code key={source}>{source}</code>)}
        </div>
      </section>

      <section className="mcp-lineage-section">
        <div className="mcp-section-heading">
          <GitMerge aria-hidden="true" />
          <div><h4>How the data connects</h4><p>Business relationships, expressed without SQL syntax.</p></div>
        </div>
        <ol className="mcp-relationship-list">
          {tool.relationships.map((relationship, index) => (
            <li key={relationship}><span>{index + 1}</span><p>{relationship}</p></li>
          ))}
        </ol>
      </section>

      <section className="mcp-output-section">
        <div className="mcp-section-heading">
          <Columns3 aria-hidden="true" />
          <div><h4>Output columns</h4><p>Fields surfaced by the tool, grouped by response section.</p></div>
        </div>
        <div className="mcp-output-groups">
          {tool.outputGroups.map((group) => (
            <section key={group.name} className="mcp-output-group">
              <header><h5>{group.name}</h5><span>{group.columns.length}</span></header>
              <div>{group.columns.map((column) => <code key={column}>{column}</code>)}</div>
            </section>
          ))}
        </div>
      </section>
    </>;
}

function ToolKindIcon({ kind }: { kind: string }): ReactNode {
  if (kind === 'Analytics') return <Database aria-hidden="true" />;
  if (kind === 'Workflow') return <GitMerge aria-hidden="true" />;
  if (kind === 'Metadata') return <TableProperties aria-hidden="true" />;
  return <Box aria-hidden="true" />;
}

function countOutputColumns(tool: McpToolCatalogEntry): number {
  return tool.outputGroups.reduce((total, group) => total + group.columns.length, 0);
}

function createDefaultMcpWorkspaceState(): McpWorkspaceState {
  return {
    query: '',
    domain: ALL_FILTER,
    kind: ALL_FILTER,
    selectedName: TOOLS[0]?.name ?? '',
    selectedWidgetTable: 'biBookingsCore',
    activeTab: 'overview'
  };
}

function normalizeMcpWorkspaceState(candidate: McpWorkspaceState): McpWorkspaceState {
  const selectedName = TOOLS.some((tool) => tool.name === candidate.selectedName)
    ? candidate.selectedName
    : TOOLS[0]?.name ?? '';
  return {
    query: candidate.query.slice(0, 512),
    domain: candidate.domain === ALL_FILTER || TOOLS.some((tool) => tool.domain === candidate.domain) ? candidate.domain : ALL_FILTER,
    kind: candidate.kind === ALL_FILTER || TOOLS.some((tool) => tool.kind === candidate.kind) ? candidate.kind : ALL_FILTER,
    selectedName,
    selectedWidgetTable: WIDGET_TABLE_NAMES.includes(candidate.selectedWidgetTable) ? candidate.selectedWidgetTable : 'biBookingsCore',
    activeTab: EXECUTABLE_TOOLS.has(selectedName) && candidate.activeTab === 'run' ? 'run' : 'overview'
  };
}

function readMcpWorkspaceState(): McpWorkspaceState {
  const defaults = createDefaultMcpWorkspaceState();
  const rawHash = window.location.hash.replace(/^#/, '');
  const [, rawQuery = ''] = rawHash.split('?', 2);
  const params = new URLSearchParams(rawQuery);
  if ([...params.keys()].length === 0) {
    try {
      const stored = JSON.parse(window.sessionStorage.getItem(MCP_WORKSPACE_STORAGE_KEY) ?? '') as Partial<McpWorkspaceState>;
      return normalizeMcpWorkspaceState({ ...defaults, ...stored });
    } catch {
      return defaults;
    }
  }

  return normalizeMcpWorkspaceState({
    query: params.get('q') ?? '',
    domain: params.get('domain') ?? ALL_FILTER,
    kind: params.get('kind') ?? ALL_FILTER,
    selectedName: params.get('tool') ?? defaults.selectedName,
    selectedWidgetTable: (params.get('table') ?? defaults.selectedWidgetTable) as WidgetTableName,
    activeTab: params.get('tab') === 'run' ? 'run' : 'overview'
  });
}

function writeMcpWorkspaceState(state: McpWorkspaceState, mode: 'push' | 'replace'): void {
  window.sessionStorage.setItem(MCP_WORKSPACE_STORAGE_KEY, JSON.stringify(state));
  const params = new URLSearchParams();
  if (state.selectedName !== TOOLS[0]?.name) params.set('tool', state.selectedName);
  if (state.query) params.set('q', state.query);
  if (state.domain !== ALL_FILTER) params.set('domain', state.domain);
  if (state.kind !== ALL_FILTER) params.set('kind', state.kind);
  if (state.activeTab === 'run') params.set('tab', 'run');
  if (state.selectedWidgetTable !== 'biBookingsCore') params.set('table', state.selectedWidgetTable);
  const hash = `#mcp-tools${params.size > 0 ? `?${params.toString()}` : ''}`;
  window.history[mode === 'push' ? 'pushState' : 'replaceState'](null, '', hash);
}

function readStoredNumber(key: string): number {
  const value = Number(window.sessionStorage.getItem(key));
  return Number.isFinite(value) && value >= 0 ? value : 0;
}
